import { getApiBaseUrl } from "../utils/getApiBaseUrl";
import { authHeaders, getAuthToken } from "./authApi";

const API_BASE_URL = getApiBaseUrl();

function normalizeCheckoutResponse(payload) {
  if (!payload || typeof payload !== "object") {
    return { url: "", alreadyOwned: false };
  }
  if (payload.success === true && payload.data && typeof payload.data === "object") {
    const d = payload.data;
    if (d.alreadyOwned) {
      return {
        alreadyOwned: true,
        redirectUrl: d.redirectUrl || "/dashboard/library",
        readUrl: d.readUrl || "/dashboard/library",
        assetSlug: d.assetSlug || "",
        editionId: d.editionId || "",
      };
    }
    return { id: d.id, url: d.url || "", alreadyOwned: false };
  }
  if (payload.alreadyOwned) {
    return {
      alreadyOwned: true,
      redirectUrl: payload.redirectUrl || "/dashboard/library",
      readUrl: payload.readUrl || "/dashboard/library",
      assetSlug: payload.assetSlug || "",
    };
  }
  return {
    id: payload.id,
    url: payload.url || "",
    alreadyOwned: false,
  };
}

/**
 * Start Stripe Checkout for a Treydora Library digital edition.
 * POST /stripe/library/create-checkout-session — requires Bearer auth.
 * @param {string} editionId Mongo ObjectId string
 * @returns {Promise<{ url?: string, id?: string, alreadyOwned?: boolean, redirectUrl?: string, readUrl?: string, assetSlug?: string }>}
 */
export async function createLibraryCheckoutSession(editionId) {
  const id = String(editionId || "").trim();
  if (!id) {
    throw new Error("Edition is required.");
  }

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Please log in to purchase.");
  }

  const response = await fetch(`${API_BASE_URL.replace(/\/$/, "")}/stripe/library/create-checkout-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({ editionId: id }),
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const message =
      payload?.message || payload?.error || "Unable to start secure checkout. Please try again.";
    throw new Error(message);
  }

  const normalized = normalizeCheckoutResponse(payload);
  if (normalized.alreadyOwned) {
    return normalized;
  }

  if (!normalized.url) {
    throw new Error("Unable to start secure checkout. Missing checkout URL.");
  }

  return normalized;
}

/**
 * Stripe Checkout — NFT comic tier (mint-later). POST /stripe/library/nft/create-checkout-session
 * @param {string} editionId
 * @param {number} [quantity] integer >= 1 (tier max per checkout enforced server-side)
 */
export async function createLibraryNftMintCheckoutSession(editionId, quantity = 1) {
  const id = String(editionId || "").trim();
  if (!id) {
    throw new Error("Edition is required.");
  }

  const q = Math.floor(Number(quantity));
  if (!Number.isInteger(q) || q < 1) {
    throw new Error("Quantity must be at least 1.");
  }

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Please log in to purchase.");
  }

  const response = await fetch(`${API_BASE_URL.replace(/\/$/, "")}/stripe/library/nft/create-checkout-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({ editionId: id, quantity: q }),
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const message =
      payload?.message || payload?.error || "Checkout could not be started.";
    throw new Error(message);
  }

  const normalized = normalizeCheckoutResponse(payload);
  if (normalized.alreadyOwned) {
    return normalized;
  }

  if (!normalized.url) {
    throw new Error("Checkout could not be started.");
  }

  return normalized;
}
